import React, { type FC, useState, useEffect } from 'react';
import reixLogo from 'images/reix_logo.png';
import { Disclosure } from '@headlessui/react';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';

import { Bars3Icon } from '@heroicons/react/24/outline';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useFilterStore } from '../../features/public-filters/application/filterStore';

type Props = {
  howItWorksHref: string;
  loginPath: string;
  signUpPath: string;
};

const LoggedOutNavBar: FC<Props> = ({ howItWorksHref, loginPath, signUpPath }) => {
  const [ searchInput, setSearchInput ] = useState('');
  const { setFilter } = useFilterStore();

  // Debounced search: update filter after user stops typing for 400ms
  // Automatically distinguishes between city (starts with letter) and zipcode (starts with digit)
  useEffect(() => {
    const timerId = setTimeout(() => {
      const trimmedSearch = searchInput.trim();

      if (!trimmedSearch) {
        // Clear both filters when input is empty
        setFilter((prevFilter) => ({
          ...prevFilter,
          zipcode: undefined,
          city: undefined,
        }));
      } else if (/^\d/.test(trimmedSearch)) {
        // Starts with a digit → zipcode search
        setFilter((prevFilter) => ({
          ...prevFilter,
          zipcode: trimmedSearch,
          city: undefined,
        }));
      } else {
        // Starts with a letter → city search
        setFilter((prevFilter) => ({
          ...prevFilter,
          zipcode: undefined,
          city: trimmedSearch,
        }));
      }
    }, 400);

    return () => clearTimeout(timerId);
  }, [ searchInput, setFilter ]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      // Immediately trigger search on Enter (bypass debounce)
      const trimmedSearch = searchInput.trim();

      if (!trimmedSearch) {
        setFilter((prevFilter) => ({
          ...prevFilter,
          zipcode: undefined,
          city: undefined,
        }));
      } else if (/^\d/.test(trimmedSearch)) {
        setFilter((prevFilter) => ({
          ...prevFilter,
          zipcode: trimmedSearch,
          city: undefined,
        }));
      } else {
        setFilter((prevFilter) => ({
          ...prevFilter,
          zipcode: undefined,
          city: trimmedSearch,
        }));
      }
    }
  };

  const loggedOutNavigation = [
    { id: 'how_it_works_id', name: 'How It Works', href: howItWorksHref },
    { id: 'login_id', name: 'Login', href: loginPath },
    { id: 'sign_up_id', name: 'Sign Up', href: signUpPath } ];

  return (
    <Disclosure as="nav" className="bg-white shadow">
      {({ open }) => (
        <>
          <div id='logged-out-nav-bar-container' className="mx-auto px-2 sm:px-4 lg:px-8">
            <div id='logged-out-nav-bar-large-screen' className="flex h-16 justify-between">
              {/* LEFT: logo + search, fixed 6px gap */}
              {/* LEFT: logo + search, fixed 6px gap */}
              <div id='logged-out-nav-bar-left-side' className="flex items-center gap-[12px] min-w-0 flex-1">
                <a id='logged-out-nav-bar-logo' href="/" className="flex items-center shrink-0">
                  <img className="h-10 w-auto" src={reixLogo} alt="REIX" />
                </a>

                {/* Search: grows, but capped; hide on very small screens if you want */}
                <div id='logged-out-nav-bar-search-container' className="relative xs:block flex-1 max-w-md pr-2">
                  <MagnifyingGlassIcon
                    id='logged-out-nav-bar-search-icon'
                    aria-hidden="true"
                    className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400"
                  />
                  <input
                    id='logged-out-nav-bar-search-input'
                    name="search"
                    type="text"
                    placeholder="Search by city or zipcode"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    className="w-full rounded-md bg-white py-1.5 pl-9 pr-3 text-sm text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600"
                  />
                </div>
              </div>

              {/* RIGHT: desktop links */}
              <div id='logged-out-nav-bar-right-side' className="hidden lg:ml-6 md:flex lg:space-x-8 md:space-x-4 h-fit self-center">
                { loggedOutNavigation.map((link) => {
                  let classes = 'inline-flex items-center px-1 pt-1 text-sm font-medium cursor-pointer';
                  if (link.id === 'sign_up_id') {
                    classes += ' ml-2 rounded-lg py-1 px-5 bg-blue-500 text-white inline-block hover:bg-slate-600';
                  } else {
                    classes += ' text-gray-500 border-b-2 border-transparent hover:border-gray-300 hover:text-gray-700';
                  }
                  return (
                    <a
                      id={link.id}
                      key={link.id}
                      href={link.href}
                      className={classes}
                    >
                      {link.name}
                    </a>
                  );
                })}
              </div>

              {/* Mobile toggle */}
              <div id='logged-out-nav-bar-mobile-toggle-container' className="flex items-center md:hidden">
                {/* Mobile menu button */}
                <Disclosure.Button className="inline-flex items-center justify-center rounded-md p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500">
                  <span className="sr-only">Open main menu</span>
                  {open ? (
                    <XMarkIcon className="block h-6 w-6" aria-hidden="true" />
                  ) : (
                    <Bars3Icon className="block h-6 w-6" aria-hidden="true" />
                  )}
                </Disclosure.Button>
              </div>

            </div>
          </div>

          <Disclosure.Panel className="lg:hidden">
            <div className="space-y-1 pb-3 pt-2">
              { loggedOutNavigation.map((link) => {
                let classes = 'block border-l-4 py-2 pl-3 pr-4 text-base font-medium   border-transparent text-gray-600 hover:border-blue-600 hover:bg-blue-50 hover:text-gray-800 cursor-pointer';

                return (
                  <Disclosure.Button
                    as="a"
                    id={link.id}
                    key={link.id}
                    href={link.href}
                    className={classes}
                  >
                    {link.name}
                  </Disclosure.Button>
                );
              })}
            </div>
          </Disclosure.Panel>
        </>
      )}
    </Disclosure>
  );
};

export default LoggedOutNavBar;
